/**
 * Восстановление после ошибки загрузки чанка сборки.
 *
 * Типичный случай: вкладка открыта до выкладки новой версии, а файлы старой
 * сборки на сервере уже удалены — ленивый импорт страницы падает с
 * «Failed to fetch dynamically imported module». Та же ошибка бывает при
 * обрыве соединения во время загрузки. В обоих случаях помогает перезагрузка:
 * браузер возьмёт свежий `index.html` и актуальные имена чанков.
 */

const RELOAD_AT_KEY = 'colver:chunk-reload-at';
/** Повторная перезагрузка раньше этого срока не делается — защита от цикла. */
const RELOAD_COOLDOWN_MS = 30_000;

/** Тексты ошибок загрузки модулей в разных браузерах и в preload-хелпере Vite. */
const CHUNK_ERROR_MARKERS = [
  'Failed to fetch dynamically imported module', // Chrome
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
  'Unable to preload CSS', // Vite: не загрузился CSS чанка
];

/** Ошибка вызвана тем, что не загрузился чанк сборки (JS или CSS). */
export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  CHUNK_ERROR_MARKERS.some((marker) => error.message.includes(marker));

/**
 * Перезагружает страницу, если за последние 30 секунд этого ещё не делали.
 * Возвращает `true`, если перезагрузка запущена. Без sessionStorage цикл
 * перезагрузок не отследить — тогда не перезагружаем.
 */
export const reloadForFreshBuild = (): boolean => {
  try {
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_AT_KEY) ?? 0);
    if (Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};

/**
 * Подписывается на `vite:preloadError` — Vite шлёт его при любой неудачной
 * загрузке ленивого чанка — и один раз перезагружает страницу. Ошибка при
 * этом летит дальше: если перезагрузка не нужна (недавно уже была), её
 * покажет страница ошибки роутера.
 */
export const installChunkReloadHandler = (): void => {
  window.addEventListener('vite:preloadError', () => {
    reloadForFreshBuild();
  });
};
